/**
 * OAuth Diagnostics Utility
 * 
 * Provides tools for OAuth error detection, diagnostics, and troubleshooting
 */

import { getDiscordConfig } from '../config/discord';
import { getGoogleConfig } from '../config/google';
import { getEnv } from './environment';

/**
 * Error types specifically for OAuth issues
 */
export enum OAuthErrorType {
  MISSING_CLIENT_ID = 'missing_client_id',
  INVALID_CLIENT_ID = 'invalid_client_id',
  REDIRECT_URI_MISMATCH = 'redirect_uri_mismatch',
  MISSING_CODE = 'missing_code',
  NETWORK_ERROR = 'network_error',
  SERVER_ERROR = 'server_error',
  UNKNOWN_ERROR = 'unknown_error',
  INITIALIZATION_ERROR = 'initialization_error'
}

/**
 * Structure for OAuth diagnostic information
 */
export interface OAuthDiagnostics {
  provider: 'discord' | 'google';
  errorType: OAuthErrorType;
  message: string;
  technicalDetails?: string;
  possibleSolutions: string[];
  hasError: boolean;
}

/**
 * Check if Discord OAuth configuration is valid
 */
export async function checkDiscordConfig(): Promise<OAuthDiagnostics> {
  const diagnostics: OAuthDiagnostics = {
    provider: 'discord',
    errorType: OAuthErrorType.UNKNOWN_ERROR,
    message: '',
    possibleSolutions: [],
    hasError: false
  };

  // Check client ID
  const config = await getDiscordConfig();
  const clientId = config.clientId;
  if (!clientId) {
    diagnostics.errorType = OAuthErrorType.MISSING_CLIENT_ID;
    diagnostics.message = 'Discord Client ID is missing';
    diagnostics.technicalDetails = 'The application is missing the Discord Client ID';
    diagnostics.possibleSolutions = [
      'Set the REACT_APP_DISCORD_CLIENT_ID environment variable with your Discord Client ID',
      'Create a Discord application in the Discord Developer Portal',
      'Make sure the environment variables are properly loaded'
    ];
    diagnostics.hasError = true;
    return diagnostics;
  }

  // Configuration seems valid
  return diagnostics;
}

/**
 * Check if Google OAuth configuration is valid
 */
export async function checkGoogleConfig(): Promise<OAuthDiagnostics> {
  const diagnostics: OAuthDiagnostics = {
    provider: 'google',
    errorType: OAuthErrorType.UNKNOWN_ERROR,
    message: '',
    possibleSolutions: [],
    hasError: false
  };

  try {
    // Get config from backend API
    const googleConfig = await getGoogleConfig();
    const clientId = googleConfig.clientId;
    
    if (!clientId) {
      diagnostics.errorType = OAuthErrorType.MISSING_CLIENT_ID;
      diagnostics.message = 'Google Client ID is missing';
      diagnostics.technicalDetails = 'The application is missing the Google Client ID';
      diagnostics.possibleSolutions = [
        'Check that the backend OAuth server is running on localhost:8080',
        'Verify the backend has the correct Google Client ID configured',
        'Make sure the backend /api/config/public endpoint is accessible'
      ];
      diagnostics.hasError = true;
      return diagnostics;
    }

    // Configuration seems valid
    return diagnostics;
  } catch (error) {
    diagnostics.errorType = OAuthErrorType.NETWORK_ERROR;
    diagnostics.message = 'Failed to fetch Google configuration from backend';
    diagnostics.technicalDetails = `Backend config fetch failed: ${error}`;
    diagnostics.possibleSolutions = [
      'Check that the backend OAuth server is running',
      'Verify network connectivity to localhost:8080',
      'Check browser console for CORS or network errors'
    ];
    diagnostics.hasError = true;
    return diagnostics;
  }
}

/**
 * Parse OAuth error from URL parameters
 */
export function parseOAuthError(url: string): OAuthDiagnostics | null {
  const urlObj = new URL(url);
  const params = new URLSearchParams(urlObj.search);
  
  // Check for Discord errors
  if (url.includes('discord.com') && params.has('error')) {
    const errorType = params.get('error');
    const diagnostics: OAuthDiagnostics = {
      provider: 'discord',
      errorType: OAuthErrorType.UNKNOWN_ERROR,
      message: 'Discord authentication error',
      possibleSolutions: [],
      hasError: true
    };
    
    if (errorType === 'invalid_client') {
      diagnostics.errorType = OAuthErrorType.INVALID_CLIENT_ID;
      diagnostics.message = 'Invalid Discord Client ID';
      diagnostics.possibleSolutions = [
        'Check your Discord Client ID in environment variables',
        'Verify your Discord application is properly configured',
        'Make sure your Discord application is not deleted or disabled'
      ];
    } else if (errorType === 'redirect_uri_mismatch') {
      diagnostics.errorType = OAuthErrorType.REDIRECT_URI_MISMATCH;
      diagnostics.message = 'Redirect URI does not match';
      diagnostics.possibleSolutions = [
        'Add your redirect URI to your Discord application\'s redirect URIs',
        'Check for typos in your redirect URI configuration'
      ];
    }
    
    return diagnostics;
  }
  
  // Check for Google errors
  if (url.includes('accounts.google.com') && params.has('error')) {
    const errorType = params.get('error');
    const diagnostics: OAuthDiagnostics = {
      provider: 'google',
      errorType: OAuthErrorType.UNKNOWN_ERROR,
      message: 'Google authentication error',
      possibleSolutions: [],
      hasError: true
    };
    
    if (errorType === 'invalid_client') {
      diagnostics.errorType = OAuthErrorType.INVALID_CLIENT_ID;
      diagnostics.message = 'Invalid Google Client ID';
      diagnostics.possibleSolutions = [
        'Check your Google Client ID in environment variables',
        'Verify your Google Cloud Console project is properly configured',
        'Make sure your OAuth client is not deleted or disabled'
      ];
    } else if (errorType === 'redirect_uri_mismatch') {
      diagnostics.errorType = OAuthErrorType.REDIRECT_URI_MISMATCH;
      diagnostics.message = 'Redirect URI does not match';
      diagnostics.possibleSolutions = [
        'Add your redirect URI to your Google OAuth client\'s authorized redirect URIs',
        'Check for typos in your redirect URI configuration'
      ];
    }
    
    return diagnostics;
  }
  
  return null;
}

/**
 * Check if OAuth environment variables are properly set
 */
export function checkOAuthEnvironment(): { discord: boolean; google: boolean } {
  return {
    discord: !!getEnv('REACT_APP_DISCORD_CLIENT_ID', ''),
    google: !!getEnv('REACT_APP_GOOGLE_CLIENT_ID', '')
  };
}

/**
 * Generate a troubleshooting guide based on diagnostics
 */
export function generateTroubleshootingGuide(diagnostics: OAuthDiagnostics): string {
  let guide = `## ${diagnostics.provider.toUpperCase()} OAuth Troubleshooting\n\n`;
  guide += `**Error**: ${diagnostics.message}\n\n`;
  
  if (diagnostics.technicalDetails) {
    guide += `**Technical Details**: ${diagnostics.technicalDetails}\n\n`;
  }
  
  guide += '**Possible Solutions**:\n\n';
  diagnostics.possibleSolutions.forEach((solution, index) => {
    guide += `${index + 1}. ${solution}\n`;
  });
  
  return guide;
}
